// src/pages/customer-panel/live-conversations/LiveChatHistoryPage.js
// Chat Session History - Table with transcript and feedback dialogs

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Rating,
    Grid,
    Tooltip,
    Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
    { value: 'queued', label: 'Queued' },
];

const STATUS_COLORS = {
    queued: 'warning',
    active: 'success',
    resolved: 'info',
    closed: 'default',
};

const cellSx = { fontFamily, fontSize: 13 };
const headCellSx = { fontFamily, fontWeight: 700, fontSize: 13, color: '#475569' };

export default function LiveChatHistoryPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    // State
    const [history, setHistory] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [agentFilter, setAgentFilter] = useState('');
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Transcript dialog
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [transcriptSession, setTranscriptSession] = useState(null);
    const [transcriptMessages, setTranscriptMessages] = useState([]);
    const [transcriptLoading, setTranscriptLoading] = useState(false);

    // Feedback dialog
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackSession, setFeedbackSession] = useState(null);

    // Fetch history
    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: page + 1,
                limit: rowsPerPage,
            };
            if (search.trim()) params.search = search.trim();
            if (statusFilter) params.status = statusFilter;
            if (agentFilter) params.agent_id = agentFilter;

            const response = await axiosInstance.get('/live-chat/history', { params });
            const resData = response.data?.data || {};
            const items = resData.items || resData.data || response.data?.sessions || [];
            const total = resData.total || response.data?.total_count || items.length;
            const agentList = resData.agents || response.data?.agents || [];

            setHistory(Array.isArray(items) ? items.map(s => ({
                ...s,
                rating: s.customer_rating || s.rating,
                feedback_comment: s.customer_comment || s.feedback_comment,
                start_time: s.started_at || s.start_time || s.created_at,
            })) : []);
            setTotalCount(total);
            if (agentList.length > 0) setAgents(agentList);
            setError(null);
        } catch (err) {
            console.error('Error fetching chat history:', err);
            setError(err.response?.data?.message || 'Failed to load chat history');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, statusFilter, agentFilter]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    // Open transcript dialog
    const handleOpenTranscript = async (session) => {
        setTranscriptSession(session);
        setTranscriptOpen(true);
        setTranscriptMessages([]);
        setTranscriptLoading(true);
        try {
            const response = await axiosInstance.get(
                `/live-chat/history/${session.session_id}`
            );
            const raw = response.data?.data || response.data?.messages || [];
            const msgs = (Array.isArray(raw) ? raw : []).map(msg => ({
                ...msg,
                sender_type: msg.sender_type || (msg.sender_name === session.agent_name ? 'agent' : 'customer'),
                created_at: msg.created_at || msg.timestamp,
            }));
            setTranscriptMessages(msgs);
        } catch (err) {
            console.error('Error fetching transcript:', err);
        } finally {
            setTranscriptLoading(false);
        }
    };

    // Open feedback dialog
    const handleOpenFeedback = (session) => {
        setFeedbackSession(session);
        setFeedbackOpen(true);
    };

    // Format duration
    const formatDuration = (seconds) => {
        if (!seconds && seconds !== 0) return '--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins > 60) {
            const hrs = Math.floor(mins / 60);
            const remainMins = mins % 60;
            return `${hrs}h ${remainMins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    // Format datetime
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '--';
        try {
            const d = new Date(dateStr);
            return d.toLocaleString([], {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return '--';
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <HistoryIcon sx={{ color: ACCENT, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                    Chat History
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search by customer, session ID..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                sx: { fontFamily, borderRadius: '10px', fontSize: 14 },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily, fontSize: 14 }}>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(0);
                                }}
                                label="Status"
                                sx={{ fontFamily, borderRadius: '10px', fontSize: 14 }}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily, fontSize: 14 }}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily, fontSize: 14 }}>Agent</InputLabel>
                            <Select
                                value={agentFilter}
                                onChange={(e) => {
                                    setAgentFilter(e.target.value);
                                    setPage(0);
                                }}
                                label="Agent"
                                sx={{ fontFamily, borderRadius: '10px', fontSize: 14 }}
                            >
                                <MenuItem value="" sx={{ fontFamily, fontSize: 14 }}>
                                    All Agents
                                </MenuItem>
                                {agents.map((agent) => (
                                    <MenuItem
                                        key={agent.agent_id || agent.id}
                                        value={agent.agent_id || agent.id}
                                        sx={{ fontFamily, fontSize: 14 }}
                                    >
                                        {agent.name || agent.agent_name || 'Unknown'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <Paper
                elevation={0}
                sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx}>Session ID</TableCell>
                                <TableCell sx={headCellSx}>Customer</TableCell>
                                <TableCell sx={headCellSx}>Agent</TableCell>
                                <TableCell sx={headCellSx}>Status</TableCell>
                                <TableCell sx={headCellSx}>Start Time</TableCell>
                                <TableCell sx={headCellSx}>Duration</TableCell>
                                <TableCell sx={headCellSx}>Rating</TableCell>
                                <TableCell sx={headCellSx} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: 14 }}>
                                            No chat history found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                history.map((session) => (
                                    <TableRow
                                        key={session.session_id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: `${ACCENT}06` },
                                        }}
                                        onClick={() => handleOpenTranscript(session)}
                                    >
                                        <TableCell sx={cellSx}>
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: ACCENT,
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                #{String(session.session_id || '').slice(-8)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {session.customer_name || session.customer_email || '--'}
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {session.agent_name || '--'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={session.status || '--'}
                                                color={STATUS_COLORS[session.status] || 'default'}
                                                size="small"
                                                sx={{ fontFamily, fontSize: 11, fontWeight: 600, height: 22 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {formatDateTime(session.start_time || session.created_at)}
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {formatDuration(session.duration || session.duration_seconds)}
                                        </TableCell>
                                        <TableCell>
                                            {session.rating ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Rating
                                                        value={session.rating}
                                                        readOnly
                                                        size="small"
                                                        precision={0.5}
                                                    />
                                                    <Typography sx={{ fontFamily, fontSize: 12, color: '#64748b' }}>
                                                        ({session.rating})
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontFamily, fontSize: 12, color: '#94a3b8' }}>
                                                    --
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="View transcript">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenTranscript(session);
                                                        }}
                                                        sx={{ color: ACCENT }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {session.rating && (
                                                    <Tooltip title="View feedback">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenFeedback(session);
                                                            }}
                                                            sx={{ color: '#f59e0b' }}
                                                        >
                                                            <StarIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!loading && history.length > 0 && (
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                )}
            </Paper>

            {/* Transcript Dialog */}
            <Dialog
                open={transcriptOpen}
                onClose={() => setTranscriptOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ChatBubbleOutlineIcon sx={{ color: ACCENT }} />
                        Chat Transcript
                    </Box>
                    <IconButton size="small" onClick={() => setTranscriptOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                {transcriptSession && (
                    <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                                    CUSTOMER
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: 13, color: '#1e293b' }}>
                                    {transcriptSession.customer_name || '--'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                                    AGENT
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: 13, color: '#1e293b' }}>
                                    {transcriptSession.agent_name || '--'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                                    STATUS
                                </Typography>
                                <Chip
                                    label={transcriptSession.status || '--'}
                                    color={STATUS_COLORS[transcriptSession.status] || 'default'}
                                    size="small"
                                    sx={{ fontFamily, fontSize: 11, fontWeight: 600, height: 20, mt: 0.3 }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                                    DURATION
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: 13, color: '#1e293b' }}>
                                    {formatDuration(transcriptSession.duration || transcriptSession.duration_seconds)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}
                <Divider />
                <DialogContent
                    sx={{
                        bgcolor: '#f8fafc',
                        p: 2.5,
                        minHeight: 300,
                        maxHeight: 500,
                        overflow: 'auto',
                    }}
                >
                    {transcriptLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={32} sx={{ color: ACCENT }} />
                        </Box>
                    ) : transcriptMessages.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: 14 }}>
                                No messages in this session
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {transcriptMessages.map((msg, idx) => {
                                const isAgent =
                                    msg.sender_type === 'agent' ||
                                    msg.sender === 'agent' ||
                                    msg.role === 'agent';
                                return (
                                    <Box
                                        key={msg.message_id || idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: isAgent ? 'flex-end' : 'flex-start',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                gap: 1,
                                                maxWidth: '75%',
                                            }}
                                        >
                                            {!isAgent && (
                                                <Avatar sx={{ width: 26, height: 26, bgcolor: '#94a3b8', fontSize: 11 }}>
                                                    <PersonIcon sx={{ fontSize: 15 }} />
                                                </Avatar>
                                            )}
                                            <Box>
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: isAgent
                                                            ? '14px 14px 4px 14px'
                                                            : '14px 14px 14px 4px',
                                                        bgcolor: isAgent ? ACCENT : '#e2e8f0',
                                                        color: isAgent ? '#fff' : '#1e293b',
                                                    }}
                                                >
                                                    <Typography sx={{ fontFamily, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}>
                                                        {msg.message || msg.content || msg.text || ''}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily,
                                                        fontSize: 10,
                                                        color: '#94a3b8',
                                                        mt: 0.3,
                                                        textAlign: isAgent ? 'right' : 'left',
                                                        px: 0.5,
                                                    }}
                                                >
                                                    {formatTime(msg.created_at || msg.timestamp)}
                                                </Typography>
                                            </Box>
                                            {isAgent && (
                                                <Avatar sx={{ width: 26, height: 26, bgcolor: '#059669', fontSize: 11 }}>
                                                    <SupportAgentIcon sx={{ fontSize: 15 }} />
                                                </Avatar>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={() => setTranscriptOpen(false)}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Dialog */}
            <Dialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StarIcon sx={{ color: '#f59e0b' }} />
                        Customer Feedback
                    </Box>
                    <IconButton size="small" onClick={() => setFeedbackOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                {feedbackSession && (
                    <DialogContent sx={{ pt: 3 }}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Typography sx={{ fontFamily, fontSize: 14, color: '#64748b', mb: 1 }}>
                                Rating for session with{' '}
                                <strong>{feedbackSession.customer_name || 'customer'}</strong>
                            </Typography>
                            <Rating
                                value={feedbackSession.rating || 0}
                                readOnly
                                size="large"
                                precision={0.5}
                                sx={{ fontSize: 40 }}
                            />
                            <Typography
                                variant="h4"
                                sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mt: 1 }}
                            >
                                {feedbackSession.rating || 0} / 5
                            </Typography>
                        </Box>

                        {feedbackSession.feedback_comment || feedbackSession.comment ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    bgcolor: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#94a3b8',
                                        mb: 1,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Customer Comment
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
                                    {feedbackSession.feedback_comment || feedbackSession.comment}
                                </Typography>
                            </Paper>
                        ) : (
                            <Typography
                                sx={{ fontFamily, fontSize: 14, color: '#94a3b8', textAlign: 'center' }}
                            >
                                No comments provided.
                            </Typography>
                        )}
                    </DialogContent>
                )}
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={() => setFeedbackOpen(false)}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
